/**
 * deviceId — client for the install's anonymous `device_id` (F-348).
 *
 * Per ADR-002 Amendment 2026-05-28 §A.1, an account-less Free install is
 * identified by a UUID v4 the **add-on** generates at first boot and
 * persists to `/data/mirlai/device_id`. The add-on (not this client) is
 * the sole source of truth — the web app never fabricates an id; it
 * fetches the one the gateway exposes.
 *
 * Cross-lane contract (web-slice spec §7.1 — **confirm with lane-A**):
 *   GET  /api/mirlai/v1/device  →  { "device_id": "<uuid-v4>" }
 *
 * The path + the local-tier header name (§7.2) live HERE so a lane-A
 * rename is a one-file edit. Until lane-A ships the endpoint it is
 * mocked via `mocks/deviceMock.ts` (MSW in tests; a dev fetch 404s,
 * which `loadDeviceId` surfaces to the caller — see `WEB_INTEGRATION.md`).
 */
import { useAuth } from "./auth-store";
import { tokenStorage } from "../features/auth/tokenStorage";

/** Gateway endpoint that returns the add-on's `device_id`. */
export const DEVICE_ENDPOINT_PATH = "/api/mirlai/v1/device";

/**
 * Header the gateway client attaches on local-tier endpoints to identify
 * an account-less install (consumed by `gateway-client.ts`, T-002).
 * Proposal — confirm with lane-A task 2 before freezing.
 */
export const DEVICE_ID_HEADER = "X-Mirlai-Device-Id";

/** Wire shape of the device endpoint response. */
export interface DeviceResponse {
  device_id: string;
}

/**
 * Fetch the install's `device_id` from the gateway. Throws on non-2xx
 * or a malformed body — the caller decides how to degrade (the A5 skip
 * path treats a failure as "stay on the pairing entry", per spec §6).
 */
export async function fetchDeviceId(): Promise<string> {
  const res = await fetch(DEVICE_ENDPOINT_PATH, { method: "GET" });
  if (!res.ok) {
    throw new Error(`device_id fetch failed: ${res.status}`);
  }
  const body = (await res.json()) as Partial<DeviceResponse>;
  if (!body || typeof body.device_id !== "string" || body.device_id.length === 0) {
    throw new Error("device_id fetch returned an invalid body");
  }
  return body.device_id;
}

/**
 * Read-through accessor for the `device_id`:
 *   1. in-memory store, then
 *   2. persisted storage (survives reload), then
 *   3. a gateway fetch (persisted + reflected into the store on success).
 *
 * Does NOT change `mode` — recording the install as account-less is the
 * caller's explicit decision (`useAuth.setAccountLess`). This keeps
 * `loadDeviceId` usable from the cloud-pairing/attach path (T-006) too,
 * where the id is needed without flipping the user out of `cloud`.
 */
export async function loadDeviceId(): Promise<string> {
  const cached = useAuth.getState().getDeviceId() ?? tokenStorage.read("deviceId");
  if (cached) {
    return cached;
  }
  const id = await fetchDeviceId();
  tokenStorage.write("deviceId", id);
  useAuth.setState({ deviceId: id });
  return id;
}
